import { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { scaled } from '../styles/theme';
import { useT } from '../i18n';

// Free-text project search, rendered in the table header. Committed via
// onChange after a 300ms debounce; the API's Zod schema requires min 2 chars,
// so 1-char drafts are held back (the last committed term keeps filtering)
// and only 0 or ≥2 chars commit. Styling mirrors SearchableSelect's pill
// trigger so the field reads as part of the same filter family.

const COMMIT_DEBOUNCE_MS = 300;
const MIN_CHARS = 2;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
`;

// Pill matches SelectButton (16px, 999px radius, same border/focus behavior).
// Right padding leaves room for the magnifier / clear button.
const Input = styled.input`
  font-family: var(--sans-stack);
  font-size: ${scaled(16)};
  line-height: 1.2;
  padding: 0.3em 2.6em 0.2em 0.8em;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: transparent;
  color: var(--color-text-default);
  outline: none;
  width: 100%;
  box-sizing: border-box;
  transition: border-color 0.2s, background-color 0.2s;

  &::placeholder { color: var(--color-text-muted); }
  &:hover { background-color: rgba(0, 0, 0, 0.03); }
  &:focus { border-color: var(--color-text-default); }
  &:focus-visible {
    outline: 0;
    box-shadow: 0 0 0 3px var(--color-focus-ring);
  }

  /* The custom clear button replaces the UA one. */
  &::-webkit-search-cancel-button { display: none; }
`;

const IconSlot = styled.span`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-text-muted);
  display: flex;
  align-items: center;
  pointer-events: none;
`;

const ClearButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--color-text-muted);
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.06);
    color: var(--color-text-default);
  }
  &:focus-visible {
    outline: 0;
    box-shadow: 0 0 0 3px var(--color-focus-ring);
  }
`;

const MagnifierIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 -960 960 960" width="20" fill="currentColor" aria-hidden="true">
    <path d="M784-120 532-372q-30 24-69 38t-83 14q-109 0-184.5-75.5T120-580q0-109 75.5-184.5T380-840q109 0 184.5 75.5T640-580q0 44-14 83t-38 69l252 252-56 56ZM380-400q75 0 127.5-52.5T560-580q0-75-52.5-127.5T380-760q-75 0-127.5 52.5T200-580q0 75 52.5 127.5T380-400Z" />
  </svg>
);

const CrossIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="18" viewBox="0 -960 960 960" width="18" fill="currentColor" aria-hidden="true">
    <path d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" />
  </svg>
);

interface SearchInputProps {
  value: string;
  onChange: (term: string) => void;
}

export function SearchInput({ value, onChange }: SearchInputProps) {
  const m = useT();
  const [draft, setDraft] = useState(value);
  const committedRef = useRef(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Adopt external changes (chip removal, "Alle zurücksetzen", spotlights).
  useEffect(() => {
    if (value !== committedRef.current) {
      committedRef.current = value;
      setDraft(value);
    }
  }, [value]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const commit = (term: string) => {
    clearTimeout(timerRef.current);
    if (term === committedRef.current) return;
    committedRef.current = term;
    onChange(term);
  };

  const handleInput = (next: string) => {
    setDraft(next);
    clearTimeout(timerRef.current);
    const trimmed = next.trim();
    if (trimmed.length === 0) {
      // Clearing should feel instant — no debounce.
      commit('');
    } else if (trimmed.length >= MIN_CHARS) {
      timerRef.current = setTimeout(() => commit(trimmed), COMMIT_DEBOUNCE_MS);
    }
    // 1 char: hold — the API rejects it, and committing '' would flicker.
  };

  const clear = () => {
    setDraft('');
    commit('');
  };

  return (
      <InputWrapper>
        <Input
          id="investments-search"
          type="search"
          value={draft}
          placeholder={m.search.placeholder}
          aria-label={m.search.aria}
          autoComplete="off"
          onChange={e => handleInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Escape' && draft !== '') {
              e.stopPropagation();
              clear();
            }
            if (e.key === 'Enter') {
              const trimmed = draft.trim();
              if (trimmed.length >= MIN_CHARS) commit(trimmed);
            }
          }}
        />
        {draft === '' ? (
          <IconSlot><MagnifierIcon /></IconSlot>
        ) : (
          <ClearButton type="button" aria-label={m.search.clear} onClick={clear}>
            <CrossIcon />
          </ClearButton>
        )}
      </InputWrapper>
  );
}
